import { FC, Fragment, useMemo } from 'react';

import { useLocation, useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';

import usePermissions from 'api/usePermissions';
import { User } from 'common/contracts';
import { ELocalStorageKeys } from 'constants/enums';
import { localStorageUtil } from 'utils/localStorageUtil';

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from 'components/ui/breadcrumb';

import { getBreadcrumbs } from './utils';

interface BreadcrumbItem {
  label: string;
  path: string;
  isActive?: boolean;
}

const Breadcrumbs: FC = () => {
  const location = useLocation();
  const params = useParams();
  const userDetails = localStorageUtil.getAny<User>(
    ELocalStorageKeys.USER_DETAILS
  );
  const { getPermissions } = usePermissions();
  const permissions = getPermissions();

  const breadcrumbs = useMemo(
    () => getBreadcrumbs(location.pathname, params, userDetails, permissions),
    [location.pathname, params, userDetails, permissions]
  );

  if (!breadcrumbs.length) {
    return null;
  }

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {breadcrumbs.map((item, index) => {
          const isLast = index === breadcrumbs.length - 1;
          const uniqueKey = `${item.path}-${item.label}-${index}`;

          return (
            <Fragment key={uniqueKey}>
              <BreadcrumbItem>
                {isLast ? (
                  <BreadcrumbPage className="text-base-foreground">
                    {item.label}
                  </BreadcrumbPage>
                ) : (
                  <BreadcrumbLink
                    asChild
                    className="text-base-mutedForeground hover:text-base-foreground"
                  >
                    <Link to={item.path}>{item.label}</Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
              {!isLast && <BreadcrumbSeparator />}
            </Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
};

export default Breadcrumbs;
